// BLISS API Service
// Handles all API calls to Lambda backend

import { apiFetch } from '../../lib/apiFetch.js';

const API_BASE_URL = 'https://w74ji2gjnb.execute-api.ap-south-1.amazonaws.com';

export const blissApi = {
  // Upload CSV and start Glue job
  async uploadFile(file, userEmail, serpKeys = [], domainOptions = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_email', userEmail);
    if (serpKeys.length > 0) {
      formData.append('serp_keys', serpKeys.join(','));
    }
    // Domain bypass: if user says their CSV already has domains
    if (domainOptions.hasDomain && domainOptions.domainColumn) {
      formData.append('has_domain', 'true');
      formData.append('domain_column', domainOptions.domainColumn);
    }

    const response = await apiFetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  },

  // Get job status
  async getJobStatus(jobRunId) {
    const response = await apiFetch(`${API_BASE_URL}/api/job-status/${jobRunId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch job status');
    }

    return response.json();
  },

  // Get job logs (accepts job_identifier directly)
  async getJobLogs(jobIdentifier) {
    const response = await apiFetch(`${API_BASE_URL}/api/logs/${jobIdentifier}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch logs');
    }

    return response.json();
  },

  // Get recent jobs
  async getRecentJobs() {
    const response = await apiFetch(`${API_BASE_URL}/api/recent-jobs`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch recent jobs');
    }

    return response.json();
  },

  // Download file — fetch a presigned S3 URL via the JWT-gated
  // /api/presigned-url endpoint, then navigate the browser to S3 directly.
  // We can't just window.location.href to /api/download/<key> because top-level
  // browser navs don't carry our Authorization: Bearer header, so they'd hit
  // the API Gateway JWT authorizer with no token and get a 401.
  async downloadFile(s3Key, downloadName) {
    const qs = downloadName ? `?download_name=${encodeURIComponent(downloadName)}` : '';
    const response = await apiFetch(`${API_BASE_URL}/api/presigned-url/${s3Key}${qs}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Failed to get download URL (${response.status})`);
    }
    const { url } = await response.json();
    window.location.href = url;
  },

  // Download job output
  async downloadJobOutput(jobRunId) {
    const data = await this.getJobStatus(jobRunId);

    if (data.output_file) {
      const downloadName = data.output_file.split('/').pop();
      await this.downloadFile(data.output_file, downloadName);
    } else {
      throw new Error('Output file not found for this job');
    }
  },

  // Get SerpAPI usage
  async getSerpUsage() {
    const response = await apiFetch(`${API_BASE_URL}/api/serp-usage`);
    if (!response.ok) {
      throw new Error(`Failed to fetch SERP usage: ${response.status}`);
    }
    return response.json();
  },

  // Check balance for a specific SerpAPI key (via our Lambda to avoid CORS)
  async checkSerpKey(apiKey) {
    const response = await apiFetch(`${API_BASE_URL}/api/serp-usage?key=${encodeURIComponent(apiKey)}`);
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `API error (${response.status})`);
    }
    return response.json();
  },

  // Get sample HTML reports for a job (up to 2)
  async getJobReports(jobRunId) {
    const response = await apiFetch(`${API_BASE_URL}/api/job-reports/${jobRunId}`);
    if (!response.ok) {
      return { reports: [] };
    }
    return response.json();
  }
};
